import { Entity } from '@backstage/catalog-model';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AppEnvironment, KNOWN_ENVIRONMENTS } from './appEnvironments';

/**
 * Live endpoints (ingress URLs) of an app-bootstrap app, per environment.
 *
 * Source of truth is ArgoCD: each environment's Application reports the URLs of
 * its Ingress in `status.summary.externalURLs`, so an endpoint is listed only
 * when it really exists (an app without ingress shows none). Apps are found by
 * the entity's `argocd/app-selector` annotation (app.sparrow.io/name=<app>) on
 * each ArgoCD instance from app-config `argocd.appLocatorMethods` — the same
 * Roadie argocd backend the ArgoCD card uses. The environment comes from the
 * Application's `app.sparrow.io/env` label (instance name as fallback).
 */

export const APP_SELECTOR_ANNOTATION = 'argocd/app-selector';

/** ArgoCD instance name (app-config) per environment, as in the Pipelines tab. */
const ENV_INSTANCE: Record<AppEnvironment, string> = {
  staging: 'default',
  production: 'production',
};

export type AppEndpoint = { env: AppEnvironment; url: string };

type ArgoApp = {
  metadata?: { labels?: Record<string, string> };
  status?: { summary?: { externalURLs?: string[] } };
};

const toHttps = (url: string) => url.replace(/^http:\/\//, 'https://').replace(/\/$/, '');

export const hasAppSelector = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[APP_SELECTOR_ANNOTATION]);

/**
 * Ingress URLs per environment, staging first. Instances that fail or have no
 * matching app are skipped. http:// is shown as https:// — every app ingress
 * listens on 443 with the BU's *.sparrowapps.com certificate.
 */
export async function fetchAppEndpoints(
  entity: Entity,
  discovery: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<AppEndpoint[]> {
  const selector = entity.metadata.annotations?.[APP_SELECTOR_ANNOTATION];
  if (!selector) return [];
  const base = await discovery.getBaseUrl('argocd');
  const found: AppEndpoint[] = [];
  await Promise.all(
    KNOWN_ENVIRONMENTS.map(async fallbackEnv => {
      try {
        const res = await fetchApi.fetch(
          `${base}/argoInstance/${encodeURIComponent(ENV_INSTANCE[fallbackEnv])}/applications/selector/${encodeURIComponent(selector)}`,
        );
        if (!res.ok) return;
        const body = (await res.json()) as { items?: ArgoApp[] | null };
        for (const app of body.items ?? []) {
          const label = app.metadata?.labels?.['app.sparrow.io/env'];
          const env = (KNOWN_ENVIRONMENTS as readonly string[]).includes(label ?? '')
            ? (label as AppEnvironment)
            : fallbackEnv;
          for (const url of app.status?.summary?.externalURLs ?? []) {
            found.push({ env, url: toHttps(url) });
          }
        }
      } catch {
        // This ArgoCD is unreachable right now: show what the other one has.
      }
    }),
  );
  const seen = new Set<string>();
  return found
    .filter(e => (seen.has(e.url) ? false : (seen.add(e.url), true)))
    .sort((a, b) => KNOWN_ENVIRONMENTS.indexOf(a.env) - KNOWN_ENVIRONMENTS.indexOf(b.env));
}
